import type { SubtitleEditorData } from '@/components/SubtitleEditor';
import { pickOppositeColor } from '@/lib/color/contrast';

export interface PaletteEditorValue {
  colors: string[];
  /** Default subtitle font color (shown as "Background color" in the palette card). */
  fontColor: string;
}

export interface PaletteSubtitleLinks {
  highlight: boolean;
  font: boolean;
  shadow: boolean;
}

export type PaletteSubtitleColorRole = keyof PaletteSubtitleLinks;

export function relinkPaletteSubtitleRole(
  links: PaletteSubtitleLinks,
  role: PaletteSubtitleColorRole,
): PaletteSubtitleLinks {
  return { ...links, [role]: true };
}

export function derivePaletteSubtitleUpdates(
  palette: PaletteEditorValue,
  links: PaletteSubtitleLinks,
): Partial<Pick<SubtitleEditorData, 'highlightColor' | 'textColor' | 'shadowColor'>> {
  return {
    ...(links.highlight && palette.colors[0]
      ? { highlightColor: palette.colors[0] }
      : {}),
    ...(links.font && palette.fontColor
      ? { textColor: palette.fontColor }
      : {}),
    ...(links.shadow && palette.fontColor
      ? { shadowColor: pickOppositeColor(palette.fontColor) }
      : {}),
  };
}

export function derivePaletteSubtitleLinks(
  palette: PaletteEditorValue,
  subtitle: Pick<SubtitleEditorData, 'highlightColor' | 'textColor' | 'shadowColor'>,
): PaletteSubtitleLinks {
  const highlight = palette.colors[0]?.toLowerCase() === subtitle.highlightColor.toLowerCase();
  const font = palette.fontColor.toLowerCase() === subtitle.textColor.toLowerCase();
  const shadow = pickOppositeColor(palette.fontColor).toLowerCase() === subtitle.shadowColor.toLowerCase();
  return { highlight, font, shadow };
}

/** Preset swatches for ColorInput: highlight, background/font color, then accents. */
export function buildPaletteColorInputPresets(palette: PaletteEditorValue): string[] {
  const seen = new Set<string>();
  const presets: string[] = [];

  const add = (color?: string) => {
    if (!color) return;
    const key = color.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    presets.push(color);
  };

  add(palette.colors[0]);
  add(palette.fontColor);
  for (const color of palette.colors.slice(1)) {
    add(color);
  }

  return presets;
}
